import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, IconButton, Box, Button, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RedTheme } from 'common/global';
import { GenericAutocomplete } from './genericAutocomplete';
import {
  AdminShareFavouriteFilterAction,
  AdminListToShareFavWithAction,
  AdminManageFilterListAction,
  AdminSharedWithFiltersAction,
  AdminDeleteSharedFavouriteAction,
} from 'TAP/redux/actions';
import { Toaster } from './Toaster';
import SharedWithErrorState from './SharedWithErrorState';
import { NUMBER_ARRAY, labels } from 'common/global/constants';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { ReactComponent as CutIcon } from 'common/assets/images/CutIcon.svg';

export const ShareFilterAdminSelectionPopup = ({
  open,
  handleClose,
  popupHeader,
  leftBtnText,
  rightBtnText,
  favName,
}) => {
  let redtheme = RedTheme;
  const ADMIN = 'Admin';
  const [adminOptions, setAdminOptions] = useState([]);
  const [adminValues, setAdminValues] = useState([]);
  const [adminChecked, setAdminChecked] = useState(false);
  const [adminSwitch, setAdminSwitch] = useState(true);
  const [loggedInUserId, setLoggedInUserId] = useState('');
  const [shareButtonDisableStatus, setShareButtonDisableStatus] = useState(true);
  const [showSharedSuccessfullyToaster, setShowSharedSuccessfullyToaster] = useState(false);
  const [adminToUnshare, setAdminToUnshare] = useState(null);
  const [openUnshareConfirm, setOpenUnshareConfirm] = useState(false);
  const [showUnshareSuccess, setShowUnshareSuccess] = useState(false);
  const [showUnshareError, setShowUnshareError] = useState(false);

  const dispatch = useDispatch();

  let getLoggedInUserDetails = useSelector((state) => state.tapUserDetailsRd);
  let adminList = useSelector((state) => state.adminListToShareFavWithRd?.adminList?.data);
  const sharedStatus = useSelector(
    (state) => state.adminShareFavouriteRd?.adminShareFavouriteFilterStatus?.data,
  );
  const { status: deleteStatus, error: deleteError } = useSelector(
    (state) => state.adminDeleteSharedFavouriteRd,
  );
  const sharedWithState = useSelector((state) => state.adminSharedWithFiltersRd);
  const sharedAdminEmails = sharedWithState?.data?.map((admin) => admin.admin_email) || [];

  // Fetch List of admins on Load
  useEffect(() => {
    dispatch(AdminListToShareFavWithAction());
  }, []);

  useEffect(() => {
    if (sharedStatus === 'Favourite Shared Successfully !') {
      setShowSharedSuccessfullyToaster(true);
      dispatch(AdminManageFilterListAction({ apiType: 'LIST' }));
      dispatch(AdminSharedWithFiltersAction({ filterName: favName }));
    }
  }, [sharedStatus, favName, dispatch]);

  useEffect(() => {
    if (deleteStatus === 'Delete successful') {
      setShowUnshareSuccess(true);
      dispatch(AdminManageFilterListAction({ apiType: 'LIST' }));
      dispatch(AdminSharedWithFiltersAction({ filterName: favName }));
      dispatch({ type: 'RESET_DELETE_SHARED_FAVOURITE_FILTER' });
    }

    if (deleteError) {
      setShowUnshareError(true);
      dispatch({ type: 'RESET_DELETE_SHARED_FAVOURITE_FILTER' });
    }
  }, [deleteStatus, deleteError, favName, dispatch]);

  useEffect(() => {
    if (open && favName) {
      dispatch({ type: 'RESET_ADMIN_SHARED_WITH_FILTERS' });
      dispatch(AdminSharedWithFiltersAction({ filterName: favName }));
    }
  }, [open, favName, dispatch]);

  useEffect(() => {
    if (!adminList || adminList.length === 0 || !loggedInUserId) return;

    const sharedEmails = new Set((sharedWithState.data || []).map((adm) => adm.admin_email));

    // Filter self so that user can not share with self
    const filteredAdminList = adminList.filter(
      (admin) => admin?.user_id !== loggedInUserId && !sharedEmails.has(admin.user_email),
    );

    setAdminOptions(filteredAdminList);
  }, [adminList, loggedInUserId, sharedWithState.data]);

  // Get Logged in User Details
  useEffect(() => {
    if (getLoggedInUserDetails && Object.keys(getLoggedInUserDetails).length) {
      setLoggedInUserId(getLoggedInUserDetails?.userDetails?.data[0].user_id);
    }
  }, [getLoggedInUserDetails]);

  // Enable and disable Share button based on if any admin has selected or not
  useEffect(() => {
    if (adminValues.length > 0) {
      setShareButtonDisableStatus(false);
    } else {
      setShareButtonDisableStatus(true);
    }
  }, [adminValues]);

  const onSuccessToasterClose = () => {
    setShowSharedSuccessfullyToaster(false);
  };

  const switchChange = (_e, type) => {
    if (type === ADMIN) {
      setAdminSwitch(!adminSwitch);
      setAdminChecked(!adminChecked);
    }
  };

  const getLabeledOptions = (option, type) => {
    if (type === ADMIN) {
      return `(${option.user_id}) ${option.full_name}`;
    }
  };

  const handleAdminStatusChange = (event, type) => {
    if (type === 'Admin') {
      setAdminValues(event);
    } else {
      //No valid request
      setAdminValues([]);
    }
  };

  const shareFavHandler = (favName, adminValues) => {
    if (sharedWithState.error) {
      return;
    }

    if (!favName || adminValues.length === 0) return;

    const adminIdsToShareWith = adminValues
      .map((admin) => admin.user_email)
      .filter((email) => !sharedAdminEmails.includes(email));

    if (adminIdsToShareWith.length === 0) return;

    dispatch(
      AdminShareFavouriteFilterAction({
        loggedInUserId: loggedInUserId,
        filterName: favName,
        shareTo: adminIdsToShareWith,
      }),
    );

    setAdminValues([]);
    setAdminSwitch(true);
    setAdminChecked(false);
  };

  const resetSelectedAdmins = () => {
    setAdminValues([]);
    setAdminSwitch(true);
    setAdminChecked(false);
  };

  const handleUnshareClick = (admin) => {
    console.log('Click under handleShareClick', admin);
    setAdminToUnshare(admin);
    setOpenUnshareConfirm(true);
  };

  const confirmUnshare = () => {
    if (!favName || !adminToUnshare?.admin_id) {
      console.error('Invalid delete payload', {
        favName,
        adminToUnshare,
      });
      return;
    }

    const payload = {
      filterName: favName,
      adminId: adminToUnshare.admin_id,
    };

    dispatch(AdminDeleteSharedFavouriteAction(payload));

    setOpenUnshareConfirm(false);
    setAdminToUnshare(null);
  };

  /* (AdminQuickFilter Autocomplete)This function will check if the item in the option list is equal to the item in the selected value 
  list, then it will return true so that it look highlighted in the Search option to indicate the end user 
  that this item is selected and also help in selection one item only one time not multiple time*/
  const getEqualityCheck = (option, value, type) => {
    return !!(
      type === 'Admin' &&
      (value === undefined || value === null || option?.user_id === value?.user_id)
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: redtheme.palette.box.white,
          borderRadius: '16px',
          width: {
            xs: '95vw',
            sm: '92vw',
            md: '900px',
          },

          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            padding: { xs: '16px 16px 12px', md: '20px 24px 16px' },
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <IconButton
            className='btn-close-popup'
            sx={{ position: 'absolute', top: 16, right: 16 }}
            onClick={() => {
              resetSelectedAdmins();

              // Locally UI resets
              setShowSharedSuccessfullyToaster(false);
              setShowUnshareSuccess(false);
              setShowUnshareError(false);
              setAdminToUnshare(null);
              setOpenUnshareConfirm(false);

              // Redux resets
              dispatch({ type: 'RESET_ADMIN_SHARED_WITH_FILTERS' });
              dispatch({ type: 'RESET_DELETE_SHARED_FAVOURITE_FILTER' });
              dispatch({ type: 'RESET_ADMIN_SHARE_FAVOURITE_STATUS' });

              handleClose();
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            component='h4'
            variant='h4'
            sx={{
              fontSize: '31px',
              fontWeight: 700,
              fontFamily: '"JohnsonDisplay-Bold"',
              lineHeight: 1.2,
            }}
          >
            {popupHeader}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '16px',
            padding: { xs: '0 16px 16px', md: '0 24px 16px' },
            flex: 1,
            minHeight: '260px',
            overflow: 'hidden',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: '0 0 38%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box
              sx={{
                '& .MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot, & .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot':
                  {
                    paddingRight: '0px !important',
                  },
                '& .MuiInputBase-root': {
                  width: '93% !important',
                },
              }}
            >
              <GenericAutocomplete
                options={adminOptions}
                values={adminValues}
                switchChangeHandler={switchChange}
                checked={adminChecked}
                getLabeledOptions={getLabeledOptions}
                handleAdminStatusChange={handleAdminStatusChange}
                getEqualityCheck={getEqualityCheck}
                type={ADMIN}
                placeholder={
                  adminSwitch
                    ? adminValues?.length
                      ? `${adminValues.length} Search`
                      : `Search`
                    : `${adminValues.length} Selected`
                }
                heading={
                  !adminSwitch ? 'Selected Admin WWID / User Name' : 'Admin WWID / User Name'
                }
                switchIndicator={adminSwitch}
              />
            </Box>
            <Typography
              sx={{
                fontSize: '12px',
                mt: '4px',
                color: redtheme.palette.fontColor.gray_orange,
                fontFamily: '"JohnsonText-Regular"',
              }}
            >
              {labels.adminsAlreadyShared}
            </Typography>
            {adminValues.length > 0 && (
              <Box
                sx={{
                  mt: '12px',
                  flex: 1,
                  minHeight: 0,
                  maxHeight: { xs: '150px', md: '200px' },
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  pr: '4px',
                  backgroundColor: '#f1efed6e',
                  padding: '8px',
                  borderRadius: '8px',
                }}
              >
                {adminValues.map((admin) => (
                  <Box
                    key={admin.user_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      backgroundColor: redtheme.palette.background.default,
                      fontSize: '13px',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        fontFamily: '"JohnsonText-Regular"',
                        fontSize: '13px',
                        color: '#212121',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                      }}
                    >
                      {`${admin.full_name} (WWID ${admin.user_id})`}
                    </Typography>

                    <Box
                      onClick={() => {
                        // Remove this admin from selected list
                        setAdminValues(adminValues.filter((a) => a.user_id !== admin.user_id));
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        flexShrink: 0,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          borderRadius: '4px',
                        },
                      }}
                    >
                      <CutIcon sx={{ width: '16px', height: '16px' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              flex: '0 0 62%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%',
              position: 'relative',
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                fontFamily: '"JohnsonText-Regular"',
                lineHeight: '16px',
                color: '#212121',
                mb: '8px',
                mt: '5px',
                flexShrink: 0,
              }}
            >
              {labels.sharedTxt}
            </Typography>
            {sharedWithState.isLoading && sharedWithState.data.length === 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '32px',
                  left: 0,
                  right: '14px',
                  height: '155px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  zIndex: 10,
                }}
              >
                <CircularProgress
                  size={24}
                  sx={{
                    color: '#81766F',
                  }}
                />
              </Box>
            )}
            {sharedWithState.isLoading && sharedWithState.data.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '32px',
                  left: 0,
                  right: '14px',
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '8px',
                  zIndex: 10,
                }}
              >
                <CircularProgress
                  size={24}
                  sx={{
                    color: '#81766F',
                  }}
                />
              </Box>
            )}
            {!sharedWithState.isLoading && sharedWithState.error && (
              <SharedWithErrorState
                height={260}
                onRetry={() => dispatch(AdminSharedWithFiltersAction({ filterName: favName }))}
              />
            )}
            {!sharedWithState.isLoading &&
              !sharedWithState.error &&
              sharedWithState.data.length === 0 && (
                <Box
                  sx={{
                    minHeight: '260px',
                    marginRight: '14px',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#F1EFED',
                      color: '#81766F',
                      height: '155px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontFamily: '"JohnsonText-Regular"',
                      fontSize: '14px',
                      width: '100%',
                    }}
                  >
                    {labels.notSharedTxt}
                  </Box>
                </Box>
              )}
            {sharedWithState.data.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: '260px',
                  maxHeight: { xs: '200px', md: '280px' },
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  pr: '4px',
                  backgroundColor: '#f1efed6e',
                  padding: '8px',
                  borderRadius: '8px',
                }}
              >
                {Array.isArray(sharedWithState?.data) &&
                  sharedWithState.data.map((admin, index) => {
                    if (!admin) return null;

                    const name = admin?.admin_name ?? '';
                    const id = admin?.admin_id ?? '';
                    const email = admin?.admin_email ?? '';

                    const formattedDate =
                      admin?.createdAt && !isNaN(new Date(admin.createdAt).getTime())
                        ? new Date(admin.createdAt).toLocaleDateString()
                        : null;

                    return (
                      <Box
                        key={email || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          boxShadow: '0px 2px 6px rgba(0,0,0,0.08)',
                          backgroundColor: redtheme.palette.background.default,
                          fontSize: '14px',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            fontFamily: '"JohnsonText-Regular"',
                            fontSize: '13px',
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            color: '#212121',
                          }}
                        >
                          {[name && id ? `${name} (${id})` : name || id, email, formattedDate]
                            .filter(Boolean)
                            .join(' | ')}
                        </Typography>

                        <IconButton
                          size='small'
                          onClick={() => handleUnshareClick(admin)}
                          sx={{
                            width: '28px',
                            height: '28px',
                            color: '#00000F',
                            flexShrink: 0,
                            '&:hover': {
                              color: redtheme.palette.error.main,
                            },
                          }}
                        >
                          <RemoveCircleOutlineIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    );
                  })}
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            padding: '16px 24px',
            justifyContent: 'flex-start',
            flexShrink: 0,
          }}
        >
          <Button
            onClick={() => {
              resetSelectedAdmins();
              setShowSharedSuccessfullyToaster(false);
              handleClose();
            }}
            sx={{
              width: '125px',
              height: '35px',
              minWidth: '120px',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '16px',
              borderRadius: '8px',
              fontFamily: '"JohnsonText-Medium"',
              textTransform: 'none',
              color: '#212121',
              border: '1px solid #212121',
              backgroundColor: 'transparent',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: 'rgba(33, 33, 33, 0.04)',
                border: '1px solid #212121',
              },
            }}
          >
            {leftBtnText}
          </Button>
          <Button
            onClick={() => shareFavHandler(favName, adminValues)}
            disabled={shareButtonDisableStatus || sharedWithState.error}
            sx={{
              width: '125px',
              height: '35px',
              minWidth: '125px',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '16px',
              borderRadius: '8px',
              textTransform: 'none',
              padding: '8px 16px',
              color: redtheme.palette.button.white,
              bgcolor: redtheme.palette.primary.main,
              '&:hover': {
                bgcolor: redtheme.palette.primary.main,
                fontFamily: '"JohnsonText-Medium"',
              },
              '&.Mui-disabled': {
                color: redtheme.palette.button.gray,
                bgcolor: 'transparent',
                border: '1px solid #D0D5DD',
                cursor: 'not-allowed',
              },
            }}
            variant='primary'
          >
            {rightBtnText}
          </Button>
        </Box>
        <Modal open={openUnshareConfirm} onClose={() => setOpenUnshareConfirm(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: redtheme.palette.box.white,
              borderRadius: '12px',
              width: '420px',
              maxWidth: '90vw',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              padding: '28px 32px',
            }}
          >
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 600,
                fontFamily: '"JohnsonDisplay-Bold"',
                color: '#212121',
                marginBottom: '16px',
                lineHeight: '1.3',
              }}
            >
              {labels.removeAccessHeader}
            </Typography>

            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '1.6',
                marginBottom: '28px',
                fontFamily: '"JohnsonText-Medium"',
                color: redtheme.palette.fontColor.gray_orange,
              }}
            >
              {labels.removeAccessSubHeader}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                onClick={() => setOpenUnshareConfirm(false)}
                sx={{
                  minWidth: '110px',
                  height: '38px',
                  fontWeight: 500,
                  fontSize: '15px',
                  borderRadius: '8px',
                  fontFamily: '"JohnsonText-Medium"',
                  textTransform: 'none',
                  color: '#212121',
                  backgroundColor: 'transparent',
                  border: '1px solid #212121',
                  padding: '8px 20px',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 33, 33, 0.04)',
                    border: '1px solid #212121',
                  },
                }}
              >
                {labels.cancelBtnText}
              </Button>

              <Button
                onClick={confirmUnshare}
                sx={{
                  minWidth: '110px',
                  height: '38px',
                  fontWeight: 500,
                  fontSize: '15px',
                  borderRadius: '8px',
                  fontFamily: '"JohnsonText-Medium"',
                  textTransform: 'none',
                  padding: '8px 20px',
                  color: redtheme.palette.button.white,
                  bgcolor: redtheme.palette.primary.main,
                  '&:hover': {
                    color: redtheme.palette.button.white,
                    bgcolor: redtheme.palette.primary.main,
                  },
                }}
                variant='primary'
              >
                {labels.unshareBtnText}
              </Button>
            </Box>
          </Box>
        </Modal>

        <Toaster
          severity='success'
          show={showUnshareSuccess}
          message={labels.unshareSuccessMessage}
          hideDuration={NUMBER_ARRAY.twoThousand}
          onClose={() => setShowUnshareSuccess(false)}
        />

        <Toaster
          severity='error'
          show={showUnshareError}
          message={labels.unshareErrorMessage}
          hideDuration={NUMBER_ARRAY.twoThousand}
          onClose={() => setShowUnshareError(false)}
        />

        <Toaster
          severity='success'
          show={showSharedSuccessfullyToaster}
          message={labels.shareFavouriteSuccessMessage}
          hideDuration={NUMBER_ARRAY.twentyFiveHundred}
          onClose={onSuccessToasterClose}
        />
      </Box>
    </Modal>
  );
};
